import React, { useRef, useState } from 'react';
import uploadGif from '@/assets/gifs/upload.gif';
import PdfIcon from '@/icons/files/PdfIcon2';
import PngIcon from '@/icons/files/PngIcon';
import JpgIcon from '@/icons/files/JpgIcon';
import JpegIcon from '@/icons/files/JpegIcon';
import { getFileExtension } from '../../../utils/helpers/helpers';

const fileIcons: Record<string, () => React.ReactElement> = {
  pdf: PdfIcon,
  jpg: JpgIcon,
  jpeg: JpegIcon,
  png: PngIcon,
};

const ACCEPTED_TYPES = '.pdf,.jpg,.jpeg,.png';
const ACCEPTED_MIME = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const MAX_FILES = 5;

interface UploadMultipleFilesProps {
  files?: File[];
  onChange?: (files: File[]) => void;
  error?: string;
  onBlur?: () => void;
}

export default function UploadMultipleFiles({
  files = [],
  onChange,
  error,
  onBlur,
}: UploadMultipleFilesProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const remainingSlots = MAX_FILES - files.length;
  const atLimit = remainingSlots <= 0;

  const fileKey = (file: File) =>
    `${file.name}-${file.size}-${file.lastModified}`;

  const handleClick = () => {
    if (atLimit) return;
    fileInputRef.current?.click();
  };

  const addFiles = (incoming: File[]) => {
    if (atLimit) return;
    const filtered = incoming
      .filter(
        (f) =>
          ACCEPTED_MIME.includes(f.type) ||
          ['pdf', 'jpg', 'jpeg', 'png'].includes(
            getFileExtension(f.name).toLowerCase()
          )
      )
      .slice(0, remainingSlots);

    const existingKeys = new Set(files.map(fileKey));
    const deduped = filtered.filter((f) => !existingKeys.has(fileKey(f)));

    if (deduped.length === 0) return;
    onChange?.([...files, ...deduped]);
    onBlur?.();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;
    addFiles(Array.from(selected));
    e.target.value = '';
  };

  const handleRemove = (index: number) => {
    const next = files.filter((_, i) => i !== index);
    onChange?.(next);
    onBlur?.();
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!atLimit) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (atLimit) return;
    const dropped = e.dataTransfer.files;
    if (dropped && dropped.length > 0) addFiles(Array.from(dropped));
  };

  return (
    <div className="w-full flex flex-col items-start gap-1">
      <label className="text-left text-xs md:text-sm lg:text-sm 3xl:text-base text-input-active-label font-semibold">
        Upload Files <span className="text-sm text-error-1">*</span>
      </label>
      <p className="text-text-primary-body text-xs md:text-sm">
        Add up to {MAX_FILES} files to help Crafter understand the project,
        goals, requirements, and supporting context.
      </p>

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`mt-2 text-[#333333] flex flex-col items-center justify-center gap-4 w-full h-fit bg-modal-input rounded-md py-8 px-4 transition-colors ${
          error
            ? 'border-0.5 border-error-2'
            : isDragging
              ? 'border border-dashed border-[var(--color-navigation-accent-3)]'
              : 'border-0.5 border-gray-3/20'
        }`}
      >
        <div className="w-fit h-fit flex flex-col items-center">
          <img
            src={uploadGif}
            alt="uploadGif"
            width="44"
            className="opacity-80"
          />

          <div className="mt-3 flex flex-col items-center text-center">
            <h3 className="text-text-primary-heading font-semibold text-sm md:text-base">
              Upload a file or drag &amp; drop it here
            </h3>
            <p className="text-xs text-text-primary-body mt-1">
              Attach requirements, strategy docs, architecture diagrams, tech
              specs, and more.
            </p>
            <p className="text-xs text-text-primary-body">
              Supported file formats: PDF, JPEG, PNG.
            </p>
          </div>

          <button
            type="button"
            onClick={handleClick}
            disabled={atLimit}
            className={`mt-5 px-6 py-2.5 text-sm rounded-lg font-semibold border border-navigation-accent-1 text-navigation-accent-1 transition-opacity ${
              atLimit ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-80'
            }`}
          >
            Browse Files
          </button>
          <input
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handleFileChange}
            onBlur={onBlur}
          />
        </div>

        {files.length > 0 && (
          <div className="w-full mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {files.map((file, index) => {
                const ext = getFileExtension(file.name).toLowerCase();
                const IconComponent = fileIcons[ext];

                return (
                  <div
                    key={fileKey(file) + index}
                    className="flex items-center justify-between text-sm bg-[#191919] border border-modal-stroke rounded-lg px-3 py-3 w-full text-text-primary-body"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {IconComponent && <IconComponent />}
                      <span className="text-xs truncate">{file.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemove(index)}
                      className="ml-2 text-[#3e3e3e] hover:text-text-primary-body transition-all duration-300 shrink-0"
                    >
                      <CloseIcon />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {error && <p className="text-xs text-error-2 mt-1">{error}</p>}
    </div>
  );
}

function CloseIcon() {
  return (
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.00016 15.6666C4.04683 15.6666 0.833496 12.4533 0.833496 8.49992C0.833496 4.54659 4.04683 1.33325 8.00016 1.33325C11.9535 1.33325 15.1668 4.54659 15.1668 8.49992C15.1668 12.4533 11.9535 15.6666 8.00016 15.6666ZM8.00016 2.33325C4.60016 2.33325 1.8335 5.09992 1.8335 8.49992C1.8335 11.8999 4.60016 14.6666 8.00016 14.6666C11.4002 14.6666 14.1668 11.8999 14.1668 8.49992C14.1668 5.09992 11.4002 2.33325 8.00016 2.33325Z"
        fill="currentColor"
      />
      <path
        d="M6.11357 10.8867C5.9869 10.8867 5.86023 10.84 5.76023 10.74C5.5669 10.5467 5.5669 10.2267 5.76023 10.0333L9.53357 6.25999C9.7269 6.06666 10.0469 6.06666 10.2402 6.25999C10.4336 6.45332 10.4336 6.77332 10.2402 6.96666L6.4669 10.74C6.37357 10.84 6.24023 10.8867 6.11357 10.8867Z"
        fill="currentColor"
      />
      <path
        d="M9.8869 10.8867C9.76023 10.8867 9.63357 10.84 9.53357 10.74L5.76023 6.96666C5.5669 6.77332 5.5669 6.45332 5.76023 6.25999C5.95357 6.06666 6.27357 6.06666 6.4669 6.25999L10.2402 10.0333C10.4336 10.2267 10.4336 10.5467 10.2402 10.74C10.1402 10.84 10.0136 10.8867 9.8869 10.8867Z"
        fill="currentColor"
      />
    </svg>
  );
}
